/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import Fuse from "fuse.js";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Box, Flex, Input, theme, Table } from "@nordcloud/gnui";
import { TabsPath } from "~/components/Customer/CustomerNavigation";
import { useShowError } from "~/components/Error/useShowError";
import { fuseOptions, highlightQueryText } from "~/components/Search/common";
import FlexContainer from "~/components/Styled/FlexContainer";
import { ACTIONS, Can } from "~/routing";
import { useAxios, useClickOutside, useQueryState } from "~/tools";

export const CardHeader = styled.h1`
  font-weight: 500;
`;

export const SearchWrap = styled.div`
  position: relative;
  width: 100%;
`;

export const Inner = styled.div`
  position: absolute;
  width: 100%;
  top: 50px;
  z-index: ${theme.zindex.masked};
`;

export const Result = styled(Table)`
  border-bottom: 1px solid ${theme.color.border.border01};
  padding: 1rem 0;
  b {
    font-weight: bold;
  }
`;

type Mode = "customers" | "services" | "employees" | "products" | "tools";

type FilteredSearchProps = {
  endpoint: string;
  redirectLink: string;
  mode: Mode;
  options: Fuse.IFuseOptions<any>;
};
type Results = {
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  id: string;
  permission: string;
  version: string;
};

function FilteredSearch({
  endpoint,
  redirectLink,
  mode,
  options,
}: FilteredSearchProps) {
  const [searchData, setSearchData] = React.useState<Results[]>([]);
  const [query, setQuery] = React.useState("");
  const [showResults, setShowResults] = React.useState(false);

  const wrapperRef = React.useRef(null);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const [, , updateQueryState] = useQueryState();

  useClickOutside(wrapperRef, () => setShowResults(false), showResults);

  let mappedSearchData = searchData;
  let keys = options.keys || [];
  if (options.keys?.includes("id")) {
    mappedSearchData = searchData.map((item) => ({
      ...item,
      replacedId: item.id.replace(/-/g, ""),
    }));
    keys = [...keys, "replacedId"];
  }

  const opts = { ...options, ...fuseOptions, keys };
  const fuse = new Fuse(mappedSearchData, opts);
  const results = fuse
    .search(query)
    .map(({ item }) => item)
    .slice(0, 50);

  const fetchSearchData = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/search/${endpoint}`);
      data.searchResults && setSearchData(data.searchResults);
    } catch (e) {
      showError(e.response);
    }
  }, []);

  React.useEffect(() => {
    fetchSearchData();
  }, [fetchSearchData]);

  return (
    <Flex justifyContent="flex-start">
      <FlexContainer grow>
        <SearchWrap ref={wrapperRef}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (mode !== "tools") {
                updateQueryState({ query, page: "0" });
                setShowResults(false);
              }
            }}
          >
            <Input
              type="search"
              placeholder="Search"
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuery(e.target.value);
                setShowResults(e.target.value.length > 0);
              }}
            />
          </form>

          {showResults && (
            <Inner key="isSearchBar">
              {results?.length > 0 && (
                <Box shadow="shadow04">
                  <CardHeader>Top results</CardHeader>
                  <Result>
                    <Table.thead>
                      <Table.tr>
                        {mode === "employees" && (
                          <Table.th css={{ width: "45%" }}>Name</Table.th>
                        )}
                        {mode === "services" && (
                          <Table.th css={{ width: "40%", textAlign: "right" }}>
                            Version
                          </Table.th>
                        )}
                        {mode === "employees" && (
                          <>
                            <Table.th css={{ width: "35%" }}>
                              First Name
                            </Table.th>
                            <Table.th
                              css={{ width: "25%", textAlign: "center" }}
                            >
                              Last Name
                            </Table.th>
                            <Table.th
                              css={{ width: "40%", textAlign: "center" }}
                            >
                              Email
                            </Table.th>
                            <Can action={ACTIONS.EDIT_EMPLOYEE}>
                              <Table.th
                                css={{ width: "15%", textAlign: "right" }}
                              >
                                Role
                              </Table.th>
                            </Can>
                          </>
                        )}
                      </Table.tr>
                    </Table.thead>
                    <Table.tbody>
                      {results?.map((item: Results) => {
                        const link =
                          mode === "customers"
                            ? `${redirectLink}/${item.id}/${TabsPath.INFO}`
                            : `${redirectLink}/${item.id}/`;

                        return (
                          <Table.tr key={item.id}>
                            {mode !== "employees" && (
                              <Table.td>
                                <Link to={link}>
                                  {highlightQueryText(item.name, query)}
                                </Link>
                              </Table.td>
                            )}
                            {mode === "services" && (
                              <Table.td css={{ textAlign: "right" }}>
                                <Link to={link}>
                                  {highlightQueryText(item.version, query)}
                                </Link>
                              </Table.td>
                            )}
                            {mode === "employees" && (
                              <>
                                <Table.td>
                                  <Link to={link}>
                                    {highlightQueryText(item.firstName, query)}
                                  </Link>
                                </Table.td>
                                <Table.td>
                                  <Link to={link}>
                                    {highlightQueryText(item.lastName, query)}
                                  </Link>
                                </Table.td>
                                <Table.td>
                                  <Link to={link}>
                                    {highlightQueryText(item.email, query)}
                                  </Link>
                                </Table.td>
                                <Can action={ACTIONS.EDIT_EMPLOYEE}>
                                  <Table.td css={{ textAlign: "right" }}>
                                    {highlightQueryText(
                                      item?.permission?.replace("_", " "),
                                      query
                                    )}
                                  </Table.td>
                                </Can>
                              </>
                            )}
                          </Table.tr>
                        );
                      })}
                    </Table.tbody>
                  </Result>
                </Box>
              )}
            </Inner>
          )}
        </SearchWrap>
      </FlexContainer>
    </Flex>
  );
}

export { FilteredSearch };
